import React, { memo } from 'react';
import cn from 'classnames';
import { noop } from 'lodash';

import { IconButton, Image } from 'components';
import { Row } from 'components/layout';
import Icon, { IconName } from 'components/Icon';
import { Checkbox } from 'components/form';

import styles from './Card.module.scss';

export interface CardProps {
  /** Custom class name */
  className?: string | null;
  /** Custom class name for image */
  imageClassName?: string | null;
  /** Custom style */
  style?: Record<string, unknown>;
  /** Card Heading */
  title: string;
  /** Primary image */
  image: string | undefined;
  /** imageSource */
  imageSource?: 'aws';
  /** Card Description */
  description?: string;
  /** Secondary image */
  descriptionIcon?: string;
  /** descriptionIconSource */
  descriptionIconSource?: 'aws';
  /** To specify if the image should be rounded */
  isRounded?: boolean;
  /** onClick handler*/
  onCardClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** load image image lazily? */
  isLazy?: boolean;
  /** delete Card */
  onDelete?: () => void;
  /** To specify if show message icon */
  isSideIcon?: boolean;
  /** Custom class name for title */
  titleClassName?: string;
  /** Side Icon Click */
  onSideIconClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Side Icon */
  sideIcon?: IconName;
  isHuzzleVerified?: boolean;
  isVerified?: boolean;
  isCheckbox?: boolean;
  checked?: boolean;
  handleCheckboxChange?: () => void;
}

export const Card: React.FC<CardProps> = memo(
  ({
    className = null, // custom class name
    style, // custom style
    title,
    image,
    description,
    descriptionIcon,
    isRounded = false,
    isLazy = true,
    onCardClick,
    imageSource,
    imageClassName,
    descriptionIconSource,
    onDelete,
    isSideIcon = false,
    titleClassName,
    onSideIconClick,
    isCheckbox = false,
    checked,
    handleCheckboxChange,
    sideIcon = 'icon_send-message',
    isHuzzleVerified,
    isVerified,
  }: CardProps) => {
    const contentClassNames = cn(
      {
        [styles.Card]: true,
      },
      className
    );
    const imgContentClass = cn(
      {
        [styles.CardImage]: true,
        [styles.CardImageRounded]: isRounded,
      },
      imageClassName
    );
    const titleContentClass = cn(styles.CardTitle, titleClassName);
    const onTrashClick = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      event.stopPropagation();
      if (onDelete) onDelete();
    };

    const initials = title
      ?.split(' ')
      .map((word) => word[0])
      .join('');

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="Card"
        onClick={onCardClick}
      >
        <header>
          {isSideIcon && (
            <div className={styles.messageIcon}>
              {isCheckbox ? (
                <Checkbox
                  checked={checked}
                  hideLabel
                  className={cn(styles.checkbox, {
                    [styles.checkboxDefault]: checked,
                  })}
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleCheckboxChange && handleCheckboxChange();
                  }}
                  onChange={noop}
                />
              ) : null}
              <IconButton
                size="mediumplus"
                iconName={sideIcon}
                className={styles.messageIconButton}
                onClick={(e) => {
                  onSideIconClick && onSideIconClick(e);
                }}
              />
            </div>
          )}
          <Row justify="space-between">
            {image ? (
              <Image
                loading={isLazy ? 'lazy' : undefined}
                className={imgContentClass}
                src={image}
                alt={title}
                source={imageSource}
              />
            ) : (
              initials && (
                <span style={style} className={styles.avatar}>
                  {initials}
                </span>
              )
            )}
            {isVerified || isHuzzleVerified ? (
              <Icon
                iconName={isHuzzleVerified ? 'icon_star' : 'tick-mark-circle'}
                size="auto"
                className={cn(
                  styles.icon,
                  isHuzzleVerified
                    ? styles.huzzleVerifiedIcon
                    : styles.verifiedIcon
                )}
              />
            ) : null}
          </Row>
        </header>
        <footer className={styles.CardFooter}>
          <div className={styles.footerMain}>
            <h3 className={titleContentClass}>{title}</h3>
            {description && (
              <div className={styles.CardDescriptionContainer}>
                {descriptionIcon && (
                  <Image
                    className={styles.CardDescriptionIcon}
                    src={descriptionIcon}
                    alt={description}
                    loading={isLazy ? 'lazy' : undefined}
                    source={descriptionIconSource}
                  />
                )}
                <p className={styles.CardDescription}>{description}</p>
              </div>
            )}
          </div>
          {onDelete ? (
            <IconButton
              iconName="trash"
              onClick={onTrashClick}
              className={styles.CardTrashIcon}
            />
          ) : null}
        </footer>
      </div>
    );
  }
);

Card.displayName = 'Card';
